import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import { getModules } from '../../services/adminModulesService.js';
import { apiGet } from '../../services/apiClient.js';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminCard from '../../components/admin/ui/AdminCard';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminStatusBadge from '../../components/admin/ui/AdminStatusBadge';
import Icon from '../../components/admin/ui/icons/Icon';

function formatRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (Number.isNaN(diff)) return '';
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

function deployCardStatusVariant(st) {
  switch (st) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    case 'in_progress':
      return 'warning';
    case 'triggered':
      return 'info';
    default:
      return 'neutral';
  }
}

function moduleStatusVariant(status) {
  if (status === 'REACHABLE') return 'info';
  if (status === 'FAIL') return 'error';
  return 'neutral';
}

function readinessSummary(module) {
  if (!module) return 'Governance unavailable';
  if (module.status_reason) return module.status_reason;
  if (module.status_label === 'READY') return 'Ready';
  if (module.status_label === 'REACHABLE') return 'Reachable';
  if (module.status_label === 'FAIL') return 'Needs attention';
  return 'Pending';
}

function openPathInNewTab(path) {
  if (!path || typeof window === 'undefined') return;
  window.open(path, '_blank', 'noopener,noreferrer');
}

const WORKSPACE_DEFINITIONS = [
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Reporting and owner financials.',
    canonicalPath: '/modules/analytics',
    moduleCapability: 'admin:analytics:read',
    legacyPath: '/admin/analytics',
    legacyCapability: 'admin:analytics:read',
    icon: 'data',
  },
  {
    id: 'clc_properties',
    title: 'Property 360',
    description: 'Property and owner records.',
    canonicalPath: '/modules/clc-properties',
    moduleCapability: 'admin:clc-properties:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'home',
  },
  {
    id: 'daily_dashboard',
    title: 'Daily Dashboard',
    description: 'EZCare data, sync health, and daily operations.',
    canonicalPath: '/modules/daily-dashboard',
    moduleCapability: 'admin:daily-dashboard:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'data',
  },
  {
    id: 'serenity',
    title: 'Serenity Intelligence',
    description: 'Cross-app Serenity question intelligence.',
    canonicalPath: '/modules/serenity',
    moduleCapability: 'admin:serenity:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'data',
  },
  {
    id: 'booking_workspaces',
    title: 'Booking Workspaces',
    description: 'Scoped third-party booking operations.',
    canonicalPath: '/modules/booking-workspaces',
    moduleCapability: 'admin:workspaces:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'users',
  },
  {
    id: 'development_hub',
    title: 'Development Hub',
    description: 'Plans, tasks, evidence, and verification.',
    canonicalPath: '/modules/development-hub',
    moduleCapability: 'admin:development-hub:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'work',
  },
  {
    id: 'clc_communications',
    title: 'CLC Communications',
    description: 'Queue, inbox, templates, and history.',
    canonicalPath: '/modules/clc-communications/queue',
    moduleCapability: 'admin:clc-communications:read',
    legacyPath: '/admin/communications',
    legacyCapability: 'admin:communications:read',
    icon: 'communications',
  },
  {
    id: 'clc_maintenance',
    title: 'CLC Maintenance',
    description: 'Action items, vendors, and readiness.',
    canonicalPath: '/modules/clc-maintenance',
    moduleCapability: 'admin:clc-maintenance:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'work',
  },
  {
    id: 'tax_review',
    title: 'Tax Review',
    description: 'Monthly tax workflow.',
    canonicalPath: '/modules/tax-review',
    moduleCapability: 'admin:modules:read',
    legacyPath: '/admin/tax-review',
    legacyCapability: 'admin:integrations:read',
    icon: 'audit',
  },
];

const SUPPORT_DEFINITIONS = [
  {
    id: 'module-governance',
    title: 'Module Governance',
    description: 'Readiness and build truth.',
    path: '/admin/modules',
    capability: 'admin:modules:read',
    actionLabel: 'Open Governance',
    icon: 'settings',
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Hostaway and diagnostics.',
    path: '/admin/api-controller',
    capability: 'admin:integrations:read',
    actionLabel: 'Open Integrations',
    icon: 'integrations',
  },
  {
    id: 'deploy',
    title: 'Deploy',
    description: 'Release controls.',
    path: '/admin/deploy',
    superadminOnly: true,
    actionLabel: 'Open Deploy',
    icon: 'rocket',
  },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { has, isSuperAdmin, loading: capsLoading } = useCapabilities();
  const [moduleData, setModuleData] = useState({
    modules: [],
    summary: null,
    runtimeBuild: null,
    loading: true,
    error: null,
  });
  const [deployData, setDeployData] = useState({
    summary: null,
    loading: true,
    error: null,
  });

  const loadDashboardData = useCallback(async () => {
    if (has('admin:modules:read')) {
      try {
        const data = await getModules();
        setModuleData({
          modules: data.modules || [],
          summary: data.summary || null,
          runtimeBuild: data.runtime_build || null,
          loading: false,
          error: null,
        });
      } catch (err) {
        setModuleData({
          modules: [],
          summary: null,
          runtimeBuild: null,
          loading: false,
          error: err?.message || 'Failed to load module governance',
        });
      }
    } else {
      setModuleData({
        modules: [],
        summary: null,
        runtimeBuild: null,
        loading: false,
        error: null,
      });
    }

    if (isSuperAdmin) {
      try {
        const data = await apiGet('/admin/deploy/status');
        if (data?.status === 'no_deploys') {
          setDeployData({
            summary: { kind: 'none' },
            loading: false,
            error: null,
          });
        } else {
          setDeployData({
            summary: {
              kind: 'deploy',
              status: data.status,
              triggered_at: data.triggered_at,
              relative: formatRelativeTime(data.triggered_at),
            },
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        setDeployData({
          summary: null,
          loading: false,
          error: err?.message || 'Failed to load deploy status',
        });
      }
    } else {
      setDeployData({
        summary: null,
        loading: false,
        error: null,
      });
    }
  }, [has, isSuperAdmin]);

  useEffect(() => {
    if (capsLoading) return;
    loadDashboardData();
  }, [capsLoading, loadDashboardData]);

  const moduleMap = useMemo(() => {
    return new Map((moduleData.modules || []).map((module) => [module.module_id, module]));
  }, [moduleData.modules]);

  const workspaceCards = useMemo(() => {
    return WORKSPACE_DEFINITIONS
      .filter((workspace) => {
        const hasModuleAccess = workspace.moduleCapability ? has(workspace.moduleCapability) : true;
        const hasLegacyAccess = workspace.legacyCapability ? has(workspace.legacyCapability) : false;
        return hasModuleAccess || hasLegacyAccess;
      })
      .map((workspace) => {
        const governanceModule = moduleMap.get(workspace.id) || null;
        const moduleEntryPath = workspace.moduleCapability && has(workspace.moduleCapability)
          ? (governanceModule?.launch_path || workspace.canonicalPath)
          : null;
        const legacyEntryPath = workspace.legacyCapability && has(workspace.legacyCapability)
          ? workspace.legacyPath
          : null;

        return {
          ...workspace,
          governanceModule,
          moduleEntryPath,
          legacyEntryPath,
        };
      });
  }, [has, moduleMap]);

  const supportCards = useMemo(() => {
    return SUPPORT_DEFINITIONS.filter((item) => {
      if (item.superadminOnly && !isSuperAdmin) return false;
      if (!item.capability) return true;
      return has(item.capability);
    });
  }, [has, isSuperAdmin]);

  const deploySummaryText = useMemo(() => {
    if (!isSuperAdmin) return null;
    if (deployData.loading) return 'Loading deploy status...';
    if (deployData.error) return 'Deploy unavailable';
    if (deployData.summary?.kind === 'none') return 'No deploys yet';
    if (deployData.summary?.kind === 'deploy') {
      const label = String(deployData.summary.status).replace(/_/g, ' ');
      return deployData.summary.relative ? `${label} • ${deployData.summary.relative}` : label;
    }
    return null;
  }, [deployData.error, deployData.loading, deployData.summary, isSuperAdmin]);

  const topLineItems = useMemo(() => {
    const items = [];
    if (moduleData.loading) {
      items.push('Loading workspaces');
      return items;
    }

    if (moduleData.error) {
      items.push('Workspace data unavailable');
      return items;
    }

    const total = moduleData.summary?.total ?? workspaceCards.length;
    const ready = moduleData.summary?.ready ?? workspaceCards.filter((workspace) => workspace.governanceModule?.status_label === 'READY').length;
    const attention = workspaceCards.filter((workspace) => {
      const label = workspace.governanceModule?.status_label;
      return label && label !== 'READY';
    }).length;

    items.push(`${total} workspaces`);
    items.push(`${ready} ready`);
    if (attention > 0) items.push(`${attention} need attention`);
    return items;
  }, [moduleData.error, moduleData.loading, moduleData.summary, workspaceCards]);

  const platformCards = useMemo(() => {
    return supportCards.map((card) => {
      const meta = card.id === 'deploy' ? deploySummaryText : card.description;
      return {
        ...card,
        meta,
      };
    });
  }, [deploySummaryText, supportCards]);

  return (
    <div className="command-center-shell">
      <AdminPageHeader
        title="Command Center"
        subtitle="Choose a workspace and move."
        showBreadcrumb={false}
        showBackToHome={false}
      />

      <div className="command-center-overview">
        <div className="command-center-topline" aria-label="Command Center summary">
          {topLineItems.map((item) => (
            <span key={item} className="command-center-topline-item">{item}</span>
          ))}
        </div>

        <div className="command-center-utility-strip" aria-label="Platform tools">
          {platformCards.map((card) => (
            <button
              key={card.id}
              type="button"
              className="command-center-utility-chip"
              onClick={() => navigate(card.path)}
            >
              <span className="command-center-utility-chip-icon">
                <Icon name={card.icon} size="sm" ariaLabel={card.title} />
              </span>
              <span className="command-center-utility-chip-copy">
                <span className="command-center-utility-chip-title">{card.title}</span>
                {card.meta ? (
                  <span className="command-center-utility-chip-meta">{card.meta}</span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </div>

      <section className="command-center-section">
        <div className="command-center-section-kicker">Workspaces</div>
        <div className="admin-grid admin-grid-cols-3">
          {workspaceCards.map((workspace) => {
            const module = workspace.governanceModule;
            const needsAttention = module?.status_label && module.status_label !== 'READY';
            const legacyLabel = workspace.legacyEntryPath ? 'Legacy Admin available' : null;

            return (
              <AdminCard key={workspace.id} density="compact" className="command-center-nav-card">
                <div className="command-center-nav-head">
                  <div className="command-center-nav-title-wrap">
                    <div className="command-center-nav-icon">
                      <Icon name={workspace.icon} size="md" ariaLabel={workspace.title} />
                    </div>
                    <div>
                      <div className="command-center-nav-title">{workspace.title}</div>
                      <div className="command-center-nav-description">{workspace.description}</div>
                    </div>
                  </div>

                  {needsAttention ? (
                    <AdminStatusBadge variant={moduleStatusVariant(module.status_label)}>
                      {readinessSummary(module)}
                    </AdminStatusBadge>
                  ) : null}
                </div>

                <div className="command-center-nav-meta">
                  {legacyLabel || 'Primary workspace'}
                  {needsAttention && module?.last_checked ? ` • checked ${formatRelativeTime(module.last_checked)}` : ''}
                </div>

                <div className="command-center-nav-footer">
                  <div className="command-center-card-actions">
                    {workspace.moduleEntryPath && (
                      <AdminButton
                        size="sm"
                        variant="primary"
                        onClick={() => openPathInNewTab(workspace.moduleEntryPath)}
                      >
                        Open
                      </AdminButton>
                    )}

                    {workspace.legacyEntryPath && (
                      <AdminButton
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(workspace.legacyEntryPath)}
                      >
                        Legacy Admin
                      </AdminButton>
                    )}
                  </div>
                </div>
              </AdminCard>
            );
          })}
        </div>
      </section>
    </div>
  );
}
